import { Injectable, signal } from '@angular/core';

import { WorkflowStatusType } from '../../core/models/workflow-status-type';

/** Who a request must be assigned to for it to appear in the queue. */
export type AssignedToFilter = 'all' | 'me' | 'unassigned';

/**
 * Holds the New Linking Requests queue filter state (status, assignee, date
 * range, and search term) so the user's selections persist across in-app
 * navigation — e.g. opening a request detail and returning to the queue.
 * Because the service is provided in root it lives for the whole SPA session;
 * the component binds its signals directly to these.
 */
@Injectable({
  providedIn: 'root',
})
export class NewLinkingRequestsFilterStateService {
  /**
   * Date range the queue opens on. Owned here rather than on the component
   * because this service holds the default; NewLinkingRequests.dateRangeOptions
   * reads it for its "Last 7 Days" entry, so the two cannot drift.
   */
  static readonly DEFAULT_DATE_RANGE_DAYS = 7;

  /** Default: all workflow statuses. */
  readonly statusFilter = signal<WorkflowStatusType | null>(null);
  /** Default: requests for any (or no) assignee. */
  readonly assignedToFilter = signal<AssignedToFilter>('all');
  /**
   * Days of history to include; null is "All Time" (no lower bound sent to the
   * API).
   */
  readonly dateRangeDays = signal<number | null>(
    NewLinkingRequestsFilterStateService.DEFAULT_DATE_RANGE_DAYS,
  );
  readonly searchTerm = signal('');
  /**
   * Search term reflected in the currently loaded data; null when unfiltered.
   * Unlike the selects (which apply live), the search box only commits here
   * when the Filter button is clicked. It lives alongside `searchTerm` so that
   * revisiting the queue reloads exactly what was applied — a term the user
   * typed but never applied must not become applied on the way back in.
   */
  readonly appliedSearchTerm = signal<string | null>(null);
}
